import { useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeftRight, X } from "lucide-react";
import { getProcessingSpeed, LOADER_ANIMATION } from "./Requirementconstants.js";
import { getVisaIdForDestination } from "../../Home/homeData/homeConstants";
import visaService from "../../../../services/visaService";
import { fetchContent } from "../../../../services/contentService";
import "./visa.css";

function ProcessingLoader({ processingTime, noCard, checkpoints, texts, speed }) {
  const [progress, setProgress] = useState(0);
  const [activeStep, setActiveStep] = useState(0);

  /* ── Use badge color from checkpoints (stored from processing badges), fall back to speed color ── */
  const badgeColor = (checkpoints.length > 0 && checkpoints[0].color) ? checkpoints[0].color : speed.color;
  const badgeBorder = (checkpoints.length > 0 && checkpoints[0].border) ? checkpoints[0].border : badgeColor;

  useEffect(() => {
    setProgress(0);
    setActiveStep(0);
    const duration = LOADER_ANIMATION.duration;
    const interval = LOADER_ANIMATION.interval;
    const steps = duration / interval;
    let current = 0;
    const timer = setInterval(() => {
      current++;
      const pct = Math.min((current / steps) * 100, 100);
      setProgress(pct);
      const step = checkpoints.findIndex((c, i) => {
        const next = checkpoints[i + 1];
        return next ? pct < next.pct : true;
      });
      setActiveStep(step < 0 ? checkpoints.length - 1 : step);
      if (current >= steps) clearInterval(timer);
    }, interval);
    return () => clearInterval(timer);
  }, [processingTime]);

  const content = (
    <>
      <div className="visa-loader-header">
        <div className="visa-loader-title">{texts.loaderTitle || "Estimated Processing Time"}</div>
        <div className="visa-loader-value" style={{ color: speed.color }}>{processingTime}</div>
      </div>

      <div className="visa-loader-speed-badge" style={{ background: speed.bg, color: speed.color }}>            <span className="visa-loader-speed-dot" style={{ background: speed.color }} />
        {speed.label}
      </div>

      <div className="visa-loader-track-wrap">
        <div className="visa-loader-track">
          <div
            className="visa-loader-fill"
            style={{ width: `${progress}%`, background: badgeColor }}
          />
          {checkpoints.map((cp, i) => (
            <div
              key={i}
              className="visa-loader-checkpoint"
              style={{ left: `${cp.pct}%` }}
            >
              <div
                className={`visa-loader-dot ${progress >= cp.pct ? "is-reached" : ""} ${activeStep === i && progress < 100 ? "is-active" : ""}`}
                style={progress >= cp.pct ? { background: badgeColor, borderColor: badgeBorder } : {}}
              >
                {progress >= cp.pct && (
                  <span className="visa-loader-check">{texts.loaderCheckmark || "\u2713"}</span>
                )}
              </div>
              <div
                className={`visa-loader-label ${progress >= cp.pct ? "is-done" : ""} ${activeStep === i && progress < 100 ? "is-current" : ""}`}
              >
                {(() => {
                  const parts = (cp.label || "").split(" ");
                  if (parts.length < 2) return cp.label;
                  return <>{parts[0]}<br />{parts.slice(1).join(" ")}</>;
                })()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  if (noCard) return content;
  return <div className="visa-loader-card">{content}</div>;
}

function CountryDropdown({ label, options, value, onChange, placeholder, dropdownText }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((c) => (c.name || "").toLowerCase().includes(q) || (c.code || "").toLowerCase().includes(q));
  }, [options, query]);

  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach((c) => {
      if (!groups[c.region]) groups[c.region] = [];
      groups[c.region].push(c);
    });
    return groups;
  }, [filtered]);

  const selected = options.find((c) => (c.code && c.code === value) || (!c.code && c.name === value));

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setQuery("");
    inputRef.current?.focus();
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (!open) setOpen(true);
    if (!val) onChange("");
  };

  const handleSelect = (code) => {
    onChange(code);
    setOpen(false);
    setQuery("");
  };

  const displayValue = open ? query : (selected ? selected.name : "");

  return (
    <div className="visa-dropdown" ref={ref}>
      <div className="visa-dropdown__label">{label}</div>
      <div className="visa-dropdown__input-wrap">
        <input
          ref={inputRef}
          className={`visa-dropdown__trigger-input${open ? " is-open" : ""}`}
          placeholder={selected ? selected.name : placeholder}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => { if (!open) setOpen(true); }}
        />

        <span className="visa-dropdown__actions">
          {selected && !open ? (
            <span
              className="visa-dropdown__clear"
              role="button"
              tabIndex={0}
              aria-label={dropdownText.clearSelectionAria || "Clear selection"}
              onClick={handleClear}
              onKeyDown={(e) => e.key === "Enter" && handleClear(e)}
            >
              <X size={14} strokeWidth={2.5} />
            </span>
          ) : (
            <span
              className={`visa-dropdown__chevron-wrap${open ? " is-open" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => setOpen((o) => !o)}
              onKeyDown={(e) => e.key === "Enter" && setOpen((o) => !o)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          )}
        </span>
      </div>

      {open && (
        <div className="visa-dropdown__menu">
          <div className="visa-dropdown__list">
            {Object.entries(grouped).map(([region, countries]) => (
              <div key={region}>
                <div className="visa-dropdown__group-label">{region}</div>
                {countries.map((c) => (
                  <button
                    key={c.code || c.name}
                    className={`visa-dropdown__item${value === (c.code || c.name) ? " is-selected" : ""}`}
                    onClick={() => handleSelect(c.code || c.name)}
                    type="button"
                  >
                    <span className="visa-dropdown__item-name">{c.name}</span>
                    <span className="visa-dropdown__item-region">{c.code || c.region}</span>
                  </button>
                ))}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="visa-dropdown__empty">{dropdownText.noResults || "No countries found"}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function MetaItem({ label, value }) {
  return (
    <div className="visa-meta-item">
      <div className="visa-meta-item__label">{label}</div>
      <div className="visa-meta-item__value">{value}</div>
    </div>
  );
}

function DocItem({ docKey, detail, docLabels, index }) {
  const label = docLabels[docKey] || docKey;
  return (
    <div className="visa-doc-item" role="listitem">
      <div className="visa-doc-item__num">{index + 1}</div>
      <div className="visa-doc-item__content">
        <div className="visa-doc-item__name">{label}</div>
        <div className="visa-doc-item__detail">{detail}</div>
      </div>
        </div>
  );
}

function normalizeRequirement(raw) {
  if (!raw) return null;
  const config = raw.config || {};
  return {
    id: raw.id,
    status: raw.status || raw.access_status,
    visaType: raw.visaType || raw.visa_type,
    processingTime: raw.processingTime || raw.processing_time,
    stayDuration: raw.stayDuration || raw.stay_duration,
    validity: raw.validity,
    fee: raw.fee || raw.fee_text,
    biometricsRequired: raw.biometricsRequired != null ? raw.biometricsRequired : raw.biometrics_required,
    interviewRequired: raw.interviewRequired != null ? raw.interviewRequired : raw.interview_required,
    applyUrl: raw.applyUrl || raw.apply_url,
    // Prefer config documents/tips (from admin panel, proper format)
    // Fall back to top-level raw fields (from API)
    documents: config.documents?.length > 0
      ? config.documents.map(d => ({ key: d.key || d.label || "", label: d.label || d.key || "", detail: d.detail || d.description || "" }))
      : (raw.documents || []).map(d => ({ key: d.doc_key || d.key, detail: d.detail || "", label: d.label || d.doc_key || d.key || "" })),
    tips: config.tips?.length > 0
      ? config.tips.map(t => t.text || t.tip_text || t.label || "")
      : (raw.tips || []),
    visaMeta: raw.visaMeta || config.visaMeta || null,
    visaFlags: raw.visaFlags || config.visaFlags || null,
    processingBadges: raw.processingBadges || config.processingBadges || null,
    loaderCheckpoints: raw.loaderCheckpoints || config.loaderCheckpoints || null,
  };
}

function VisaResults({ fromCode, toCode, allCountries, texts, docLabels, checkpoints, fallbackDocs }) {
  const navigate = useNavigate();
  const fromCountry = allCountries.find((c) => (c.code && c.code === fromCode) || (!c.code && c.name === fromCode));
  const toCountry = allCountries.find((c) => (c.code && c.code === toCode) || (!c.code && c.name === toCode));
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [showDocOverlay, setShowDocOverlay] = useState(false);
  const [showTipOverlay, setShowTipOverlay] = useState(false);
  const DOCS_INITIAL = 10;
  const TIPS_INITIAL = 4;

  useEffect(() => {
    let active = true;
    setLoaded(false);
    setData(null);
    visaService
      .getRequirement(fromCode, toCode)
      .then((r) => {
        if (active) setData(normalizeRequirement(r?.data || null));
      })
      .catch(() => {
        if (active) setData(null);
      })
      .finally(() => {
        if (active) setLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [fromCode, toCode]);

  if (!loaded) {
    return (
      <div className="visa-results">
        <div className="visa-no-data">
          <div className="visa-no-data__title">Loading requirement...</div>
        </div>
      </div>
    );
  }

  const noDataTitle = texts.noDataFallbackTitle || "Route Data Coming Soon";
  const noDataDocsLabel = texts.noDataFallbackDocsLabel || "General documents typically required";
  const noDataText = texts.noDataText ? texts.noDataText.split(".")[0] + "." : "";

  if (!data) {
    return (
      <div className="visa-results">
        <div className="visa-no-data">
          <div className="visa-no-data__title">{noDataTitle}</div>
          <div className="visa-no-data__desc">{noDataText}</div>
          <div className="visa-no-data__docs-wrap">
            <div className="visa-no-data__docs-label">{noDataDocsLabel}</div>
            <div className="visa-doc-list">
              {fallbackDocs.map((doc, i) => (
                <DocItem key={doc.key} docKey={doc.key} detail={doc.detail} docLabels={docLabels} index={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const speed = getProcessingSpeed(data.processingTime);
  const effectiveCheckpoints = data.loaderCheckpoints || checkpoints;
  const effectiveBadge = (data.processingBadges && data.processingBadges.length > 0)
    ?    data.processingBadges.find(b => b.label.toLowerCase().includes(speed.label.toLowerCase())) || data.processingBadges[0]
    : null;
  const loaderSpeed = effectiveBadge ? { ...speed, color: effectiveBadge.color || speed.color, bg: effectiveBadge.bg || speed.bg, label: effectiveBadge.label || speed.label } : speed;
  const loaderText = {
    loaderTitle: texts.loaderTitle,
    loaderLabelSuffix: texts.loaderLabelSuffix,
    loaderCheckmark: texts.loaderCheckmark,
  };

  return (
    <>
    <div className="visa-results">
      <div className="visa-results-left">
        <div className="visa-docs-card aos-init aos-animate" data-aos="fade-up" data-aos-delay={100}>
            <div className="visa-card-header">
          <div>
            <div className="visa-card-header__title">{texts.documentsCardTitle || "Required Documents"}</div>
            <div className="visa-card-header__subtitle">{texts.documentsCardSubtitle || "Prepare all documents before applying"}</div>
          </div>
          <div className="visa-card-header__count">{(texts.documentsCardCountTemplate || "{count} documents").replace("{count}", data.documents.length)}</div>
        </div>
        <div className="visa-doc-list">
          {data.documents.slice(0, DOCS_INITIAL).map((doc, i) => (
            <DocItem key={doc.key} docKey={doc.key} detail={doc.detail} docLabels={docLabels} index={i} />
          ))}
        </div>
        {data.documents && data.documents.length > DOCS_INITIAL && (
          <button type="button" className="visa-view-toggle" onClick={() => setShowDocOverlay(true)}>
            View More
          </button>
        )}
      </div>
      </div>

      <div className="visa-results-right">
        <div className="visa-overview-card aos-init aos-animate" data-aos="fade-up">
        <div className="visa-overview-header">
          <div className="visa-route-display">
            <div className="visa-route-country">
              <div className="visa-route-country__label">{texts.overviewPassport || "Passport"}</div>
              <div className="visa-route-country__name">{fromCountry?.name}</div>
            </div>
            <div className="visa-route-arrow">
              <ArrowRight size={18} strokeWidth={1.5} color="var(--clr-navy)" />
            </div>
            <div className="visa-route-country">
              <div className="visa-route-country__label">{texts.overviewDestination || "Destination"}</div>
              <div className="visa-route-country__name">{toCountry?.name}</div>
            </div>
          </div>
          <button
            type="button"
            className="visa-apply-btn"
            style={{margin:"0 0 0 auto",alignSelf:"center",flexShrink:0}}
            onClick={() => navigate(`/visa/${getVisaIdForDestination(toCountry?.name || "")}`)}
          >
            Apply Now
          </button>
        </div>

        <div className="visa-meta-grid">
          {data.visaMeta && data.visaMeta.length > 0 ? (
            data.visaMeta.map((meta, i) => (
              <MetaItem key={meta.key || i} label={meta.label} value={meta.value} />
            ))
          ) : (
            <>
              <MetaItem label={texts.overviewStayDuration || "Stay Duration"} value={data.stayDuration} />
              <MetaItem label={texts.overviewVisaValidity || "Visa Validity"} value={data.validity} />
              <MetaItem label={texts.overviewVisaFee || "Visa Fee"} value={data.fee} />
              <MetaItem label={texts.overviewBiometrics || "Biometrics"} value={data.biometricsRequired ? (texts.resultsRequired || "Required") : (texts.resultsNotRequired || "Not Required")} />
            </>
          )}
        </div>

        <div className="visa-flags-row">
          {data.visaFlags && data.visaFlags.length > 0 && data.visaFlags.map((flag, i) => (
            <span
              key={i}
              className="visa-flag-chip"
              style={{ background: flag.bg || "var(--clr-surface)", color: flag.text || "var(--clr-ink)", borderColor: flag.border || "var(--clr-border)" }}
            >
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: flag.dot || flag.text || "currentColor", display: "inline-block", flexShrink: 0 }} />
              {flag.label}
            </span>
          ))}
        </div>

        <div className="visa-loader-section">
          <ProcessingLoader noCard processingTime={data.processingTime} checkpoints={effectiveCheckpoints} texts={loaderText} speed={loaderSpeed} />
        </div>

        {data.tips && data.tips.length > 0 && (
          <div className="visa-tips-section">
            <div className="visa-card-header">
              <div>
                <div className="visa-card-header__title">{texts.tipsCardTitle || "Application Tips"}</div>
                <div className="visa-card-header__subtitle">{texts.tipsCardSubtitle || "Improve your approval chances"}</div>
              </div>
            </div>
            <div className="visa-tips-list">
              {data.tips.slice(0, TIPS_INITIAL).map((tip, i) => (
                <div key={i} className="visa-tip-item">
                  <span className="visa-tip-item__bullet">{i + 1}</span>
                  <span className="visa-tip-item__text">{tip}</span>
                </div>
              ))}
            </div>
            {data.tips.length > TIPS_INITIAL && (
          <button type="button" className="visa-view-toggle" onClick={() => setShowTipOverlay(true)}>
                View More
              </button>
            )}
          </div>
        )}
      </div>
      </div>
      </div>

    {showDocOverlay && createPortal(
      <div className="visa-overlay" onClick={() => setShowDocOverlay(false)}>
        <div className="visa-overlay__content" onClick={(e) => e.stopPropagation()}>
          <div className="visa-overlay__header">
            <span>{texts.documentsCardTitle || "Required Documents"}</span>
            <button className="visa-overlay__close" onClick={() => setShowDocOverlay(false)}>✕</button>
          </div>
          <div className="visa-overlay__body">
            <div className="visa-doc-list">
              {data.documents.map((doc, i) => (
                <DocItem key={doc.key} docKey={doc.key} detail={doc.detail} docLabels={docLabels} index={i} />
              ))}
            </div>
          </div>
        </div>
      </div>,
      document.body
    )}

    {showTipOverlay && createPortal(
      <div className="visa-overlay" onClick={() => setShowTipOverlay(false)}>
        <div className="visa-overlay__content" onClick={(e) => e.stopPropagation()}>
          <div className="visa-overlay__header">
            <span>{texts.tipsCardTitle || "Application Tips"}</span>
            <button className="visa-overlay__close" onClick={() => setShowTipOverlay(false)}>✕</button>
          </div>
          <div className="visa-overlay__body">
            <div className="visa-tips-list">
              {data.tips.map((tip, i) => (
                <div key={i} className="visa-tip-item">
                  <span className="visa-tip-item__bullet">{i + 1}</span>
                  <span className="visa-tip-item__text">{tip}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>,
      document.body
    )}
    </>
  );
}

export default function Requirements() {
  const [content, setContent] = useState(null);
  const [fromCode, setFromCode] = useState("");
  const [toCode, setToCode] = useState("");
  const [allCountries, setAllCountries] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      const [section, pass, dest] = await Promise.all([
        fetchContent("requirements"),
        visaService.listPassportCountries(),
        visaService.listDestinationCountries(),
      ]);
      if (!active) return;
      if (section?.content) setContent(section.content);
      const dbCountries = [...(pass?.data || pass || []), ...(dest?.data || dest || [])];
      const codeMap = {};
      dbCountries.forEach(c => { if (c.code) codeMap[c.name] = c.code; });
      const contentCountries = section?.content?.countries || [];
      let countries;
      if (contentCountries.length > 0) {
        countries = contentCountries.map(c => ({
          ...c,
          code: c.code || codeMap[c.name] || "",
        }));
      } else {
        const seen = new Set();
        countries = dbCountries.filter(c => {
          const k = c.code || c.name;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
      }
      countries.sort((a, b) => (a.region || "").localeCompare(b.region || "") || a.name.localeCompare(b.name));
      setAllCountries(countries);
    })();
    return () => { active = false; };
  }, []);

  const texts = useMemo(() => {
    const obj = {};
    (content?.texts || []).forEach(t => { obj[t.key] = t.value; });
    return obj;
  }, [content]);

  const docLabels = useMemo(() => {
    const obj = {};
    (content?.labels || []).forEach(l => {
      const docKeys = ["passport", "photo", "bankStatement", "itr", "flightTicket", "hotelBooking", "insurance", "invitationLetter", "employmentLetter", "noc", "birthCertificate", "marriageCertificate", "sponsorLetter", "coverLetter", "itin", "visaFee", "biometrics", "medicalCert", "educationDocs"];
      if (docKeys.includes(l.key)) obj[l.key] = l.value;
    });
    return obj;
  }, [content]);

  const fallbackDocs = content?.fallbackDocs || [];
  const checkpoints = content?.loaderCheckpoints || [];

  const dropdownText = {
    searchPlaceholder: texts.dropdownSearchPlaceholder,
    noResults: texts.dropdownNoResults,
    clearSelectionAria: texts.dropdownClearSelectionAria,
    clearSearchAria: texts.dropdownClearSearchAria,
  };

  const handleSwap = () => {
    const prevFrom = fromCode;
    const prevTo = toCode;
    setFromCode(prevTo);
    setToCode(prevFrom);
  };

  const showResults = fromCode && toCode && fromCode !== toCode;
  const sameCountryError = fromCode && toCode && fromCode === toCode;

  return (
    <div className="visa-app">
      <section className="visa-hero" data-aos="fade-up">
        <h1 className="visa-hero__title">
          {texts.heroTitle || "Visa Requirements"}<em> {texts.heroHighlight || "Instantly"}</em>
        </h1>
        <p className="visa-hero__sub">
          {texts.heroSubtitle || "Select your passport country and destination to get complete visa requirements."}
        </p>
      </section>

      <div className="visa-search-panel" data-aos="fade-up">
        <div className="visa-search-card">
          <div className="visa-search-card__title">{texts.searchPanelTitle || ""}</div>
          <div className="visa-search-row">
            <CountryDropdown
              label={texts.searchPanelPassportLabel || "Your Passport"}
              options={allCountries}
              value={fromCode}
              onChange={setFromCode}
              placeholder={texts.searchPlaceholderFrom || "Search passport country..."}
              dropdownText={dropdownText}
            />

            <div className="visa-search-swap-wrap">
              <button
                className="visa-swap-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleSwap();
                }}
                type="button"
                aria-label={texts.swapButtonAriaLabel || "Swap countries"}
                title={texts.swapButtonTitle || "Swap countries"}
              >
                <ArrowLeftRight size={16} strokeWidth={2} />
              </button>
            </div>

            <CountryDropdown
              label={texts.searchPanelDestinationLabel || "Destination Country"}
              options={allCountries}
              value={toCode}
              onChange={setToCode}
              placeholder={texts.searchPlaceholderTo || "Search destination..."}
              dropdownText={dropdownText}
            />
          </div>
        </div>
      </div>

      <main className="visa-content" data-aos="fade-up">
        {sameCountryError ? (
          <div className="visa-empty">
            <div className="visa-empty__title">Same Country Selected</div>
            <div className="visa-empty__desc">Passport and Destination cannot be the same country. Please select a different destination.</div>
          </div>
        ) : showResults ? (
          <VisaResults
            key={`${fromCode}-${toCode}`}
            fromCode={fromCode}
            toCode={toCode}
            allCountries={allCountries}
            texts={texts}
            docLabels={docLabels}
            checkpoints={checkpoints}
            fallbackDocs={fallbackDocs}
          />
        ) : (
          <div className="visa-empty">
            <div className="visa-empty__title">{texts.emptyStateTitle || "Where are you headed?"}</div>
            <div className="visa-empty__desc">{texts.emptyStateText || "Choose your passport and destination for instant travel guidance."}</div>
          </div>
        )}
      </main>


    </div>
  );
}
